import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import { AttachFile } from "@mui/icons-material";
import PropTypes from "prop-types";
import { backendGet } from "../../services/dataService.js";
import { useSnackbar } from "notistack";
import OutgoingMailStatusChip from "./OutgoingMailStatusChip.jsx";
import { EntityAvatar } from "../../theme/components/index.js";

const CATEGORY_LABELS = {
  claim_communication: "Schaden-Kommunikation",
  reminder: "Erinnerung",
  notification: "Benachrichtigung",
  password_reset: "Passwort-Reset",
  two_factor: "2FA-Code",
  user_welcome: "Willkommen",
  system: "System",
  unknown: "Unbekannt",
};

export default function MailDetailPanel({ mail, isIncoming = false }) {
  const { enqueueSnackbar } = useSnackbar();

  const isOutgoing = !isIncoming;

  const downloadAttachment = async (attachment) => {
    try {
      const endpoint = isOutgoing
        ? `data/outgoing-mail-attachments/${attachment.id}`
        : `data/incoming-mail-attachments/${attachment.id}`;

      const response = await backendGet(endpoint, {
        responseType: "blob",
        enqueueSnackbar,
      });

      const base64Data = response.data.data;

      // Base64 dekodieren
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: attachment.mimetype || attachment.mime_type,
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        attachment.filename || attachment.original_filename
      );
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      enqueueSnackbar("Fehler beim Herunterladen des Anhangs", {
        variant: "error",
      });
    }
  };

  const formatRecipients = (recipients) => {
    if (!recipients || !Array.isArray(recipients) || recipients.length === 0) {
      return "-";
    }
    return recipients
      .map((r) => {
        if (typeof r === "string") return r;
        return r.name ? `${r.name} <${r.email}>` : r.email;
      })
      .join(", ");
  };

  if (!mail) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          Wählen Sie eine E-Mail aus, um die Details anzuzeigen.
        </Typography>
      </Box>
    );
  }

  // determine sender/display fields based on mail type
  const fromValue = isOutgoing
    ? mail.sender_name
      ? `${mail.sender_name} <${mail.sender_email}>`
      : mail.sender_email
    : mail.from_name
      ? `${mail.from_name} <${mail.from_email}>`
      : mail.from_email;

  // for outgoing mails, show recipients
  const showRecipients =
    isOutgoing && mail.to && Array.isArray(mail.to) && mail.to.length > 0;

  const isHtml =
    mail.body && /<!DOCTYPE|<html|<body|<div|<p|<table|<span/i.test(mail.body);

  const incomingSender = [mail.from_name, mail.from_email]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box sx={{ height: "100%", overflowY: "auto", p: 3 }}>
      {/* Incoming: click-dummy header (avatar + subject + sender · email + date). */}
      {isIncoming ? (
        <Box sx={{ display: "flex", gap: "14px", alignItems: "flex-start", mb: 2 }}>
          <EntityAvatar
            label={mail.from_name || mail.from_email || "?"}
            variant="rounded"
            size={40}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#1B2C45",
                lineHeight: 1.25,
              }}
            >
              {mail.subject}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#6b7280", mt: "3px" }}>
              {incomingSender || "-"}
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: "#9ca3af", mt: "2px" }}>
              {mail.sent_at
                ? new Date(mail.sent_at).toLocaleString("de-DE")
                : "-"}
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {mail.subject}
          </Typography>

          {/* category chip for outgoing mails */}
          {mail.category && (
            <Chip
              label={CATEGORY_LABELS[mail.category] || mail.category}
              size="small"
              variant="outlined"
              sx={{ mb: 1, mr: 1 }}
            />
          )}

          {mail.status && (
            <OutgoingMailStatusChip status={mail.status} sx={{ mb: 1, mr: 1 }} />
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Von: {fromValue}
          </Typography>
        </>
      )}

      {showRecipients && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          An: {formatRecipients(mail.to)}
        </Typography>
      )}

      {isOutgoing && mail.cc && mail.cc.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          CC: {formatRecipients(mail.cc)}
        </Typography>
      )}

      {isOutgoing && mail.bcc && mail.bcc.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          BCC: {formatRecipients(mail.bcc)}
        </Typography>
      )}

      {isOutgoing && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Am:{" "}
          {mail.sent_at ? new Date(mail.sent_at).toLocaleString("de-DE") : "-"}
        </Typography>
      )}

      {isHtml ? (
        <Box
          component="iframe"
          srcDoc={mail.body}
          sandbox="allow-same-origin"
          sx={{
            mt: 2,
            mb: 3,
            width: "100%",
            minHeight: "20rem",
            border: "none",
          }}
        />
      ) : (
        <Typography
          variant="body1"
          sx={{ mt: 2, whiteSpace: "pre-wrap", mb: 3 }}
        >
          {mail.body}
        </Typography>
      )}

      {mail.attachments && mail.attachments.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Anhänge:
          </Typography>
          <List>
            {mail.attachments.map((attachment) => (
              <ListItem
                key={attachment.id}
                sx={{
                  px: 2,
                  py: 1,
                  mb: 1,
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                onClick={() => downloadAttachment(attachment)}
              >
                <ListItemIcon sx={{ minWidth: "40px" }}>
                  <AttachFile />
                </ListItemIcon>
                <ListItemText
                  primary={attachment.filename || attachment.original_filename}
                  secondary={`${attachment.size || attachment.filesize || "N/A"} - ${attachment.mimetype || attachment.mime_type || "N/A"}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}

MailDetailPanel.propTypes = {
  mail: PropTypes.object,
  isIncoming: PropTypes.bool,
};
